import { useLocation } from 'react-router-dom';
import { ComplexValue } from 'app/types-shared';
import {
  Filter, CustomFilterType, FilterType, PredefinedFilter, PredefinedFilterType,
  FilterCommonValues
} from 'app/area/search-filters/filters/types';
import { allPredefinedFilters, filterId, fieldType } from 'app/area/search-filters/constants';
import { FavouriteAdminTranslation } from 'app/area/favourite-administration/constants';
import { endpoints } from 'app/area/favourite-administration/list/endpoints';
import { getFieldId } from 'app/area/search-filters/filters/selectors';
import { queryFilters } from 'app/area/favourite-administration/list/constants';

export const favouriteQueryFilters = (translation: FavouriteAdminTranslation): ComplexValue<string>[] => [
  { value: queryFilters.folderPath, description: translation.search_folder_path }
];

export const predefinedFilters = (translation: FavouriteAdminTranslation): PredefinedFilter => ({
  type: PredefinedFilterType.includeExclude,
  values: [
    { value: allPredefinedFilters.excludeInactiveOwners, description: translation.disabled_owners },
    { value: allPredefinedFilters.excludeUsedInDashboard, description: translation.used_in_dashboard },
    { value: allPredefinedFilters.excludeUsedInCompanyReport, description: translation.used_in_company_report },
    { value: allPredefinedFilters.excludeSharedInReport, description: translation.shared_reports },
    { value: allPredefinedFilters.excludeMismatchedOwners, description: translation.mismatched_owners }
  ]
});

export const defaultFilters = (): { [key: string]: FilterCommonValues<string>[] } => {
  const search = useLocation().search;
  const urlSearchParams = new URLSearchParams(search);
  const favouriteIdRootValue = urlSearchParams.get(getFieldId(filterId.favouriteId, fieldType.root));

  return {
    ...(!!favouriteIdRootValue && { [getFieldId(filterId.favouriteId, fieldType.root)]: [{ value: favouriteIdRootValue }] })
  };
};

// eslint:disable-next-line:max-func-body-length
export const favouriteFilters = (
  translation: FavouriteAdminTranslation
): Filter[] => [
  {
    id: filterId.favouriteId,
    label: translation.code,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.inputIntegerFilter,
        includeInSummary: true
      }
    ],
    type: FilterType.TEXT_RANGE
  },
  {
    id: filterId.createdDate,
    label: translation.created_date,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateSpecificPeriod,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE,
    options: {
      dateToAsEndOfDay: true
    }
  },
  {
    id: filterId.modifiedDate,
    label: translation.modiefied_date,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateSpecificPeriod,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE,
    options: {
      dateToAsEndOfDay: true
    }
  },
  {
    id: filterId.owner,
    label: translation.owner,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        isHierarchy: false,
        includeDisabledLabel: translation.owner_include_disabled,
        optionsEndpoint: endpoints.owner
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.sharedWithUserGroup,
    label: translation.shared_user_group,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeHierarchyParents,
        isHierarchy: true,
        optionsEndpoint: endpoints.sharedWithUserGroupFilter
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.sharedWithUser,
    label: translation.shared_user,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        isHierarchy: false,
        optionsEndpoint: endpoints.sharedWithUser
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.favouriteType,
    label: translation.type_of_report,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        isHierarchy: false,
        optionsEndpoint: endpoints.favouriteType
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.reportType,
    label: translation.report_type,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.code,
        isHierarchy: false,
        optionsEndpoint: endpoints.reportType
      }
    ],
    type: FilterType.SELECTED_IDS
  }
].filter(filter => !!filter);
